// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/components/SettingsSection';
import styles from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTab.module.css';
import Guilds from '@app/features/guild/state/Guilds';
import type {FormInputs} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import {MessageNotifications} from '@app/vendor/constants/NotificationConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import type React from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {Controller} from 'react-hook-form';
export const DefaultNotificationsSection: React.FC<{
	form: UseFormReturn<FormInputs>;
	canManageGuild: boolean;
	guildId: string;
}> = ({form, canManageGuild, guildId}) => {
	const {t} = useLingui();
	const guild = Guilds.getGuild(guildId)!;
	return (
		<SettingsSection
			title={<Trans>Default Notifications</Trans>}
			description={
				<Trans>
					Choose what new members get by default before they customize notifications. For larger communities,
					&quot;Mentions only&quot; is enforced.
				</Trans>
			}
		>
			{guild.isLargeGuild && (
				<WarningAlert>
					<Trans>
						Communities with over 250 people are forced onto the &quot;mentions only&quot; setting. Your original
						setting is preserved and will be restored if the community drops below 250 members.
					</Trans>
				</WarningAlert>
			)}
			<Controller
				name="default_message_notifications"
				control={form.control}
				render={({field}) => (
					<RadioGroup
						className={styles.notificationOptions}
						value={guild.isLargeGuild ? MessageNotifications.ONLY_MENTIONS : field.value}
						onChange={field.onChange}
						disabled={!canManageGuild || guild.isLargeGuild}
						aria-label={t`Default notification settings`}
						options={[
							{
								value: MessageNotifications.ALL_MESSAGES,
								name: t`All Messages`,
								desc: t`Members receive notifications for every message`,
							},
							{
								value: MessageNotifications.ONLY_MENTIONS,
								name: t`Mentions Only`,
								desc: t`Members are notified only when mentioned`,
							},
						]}
					/>
				)}
			/>
		</SettingsSection>
	);
};
